// src/portal_pagos/PayAlicuotaPayPal.js
// ══════════════════════════════════════════════════════════════════
// VERSIÓN SIMPLIFICADA - Usa PayPal.me (solo redirección a link)
// ══════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Alert, Spinner } from 'react-bootstrap';
import {
    FaPaypal,
    FaCheckCircle,
    FaTimesCircle,
    FaInfoCircle,
    FaUser,
    FaBuilding,
    FaExternalLinkAlt,
    FaUpload,
    FaReceipt
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';

const PayAlicuotaPayPal = ({ show, onHide, payment, user, onPaymentSuccess }) => {
    // ═══════════════════════════════════════════════════════════════
    // ESTADOS
    // ═══════════════════════════════════════════════════════════════
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paypalMeUsername, setPaypalMeUsername] = useState(null);
    const [paymentInitiated, setPaymentInitiated] = useState(false);

    // ═══════════════════════════════════════════════════════════════
    // FUNCIONES DE UTILIDAD
    // ═══════════════════════════════════════════════════════════════

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || isNaN(Number(amount))) {
            return '$0.00';
        }
        return `$${Number(amount).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleDateString('es-EC', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return 'N/A';
        }
    };

    // ═══════════════════════════════════════════════════════════════
    // CARGAR CONFIGURACIÓN PAYPAL.ME DEL ADMIN
    // ═══════════════════════════════════════════════════════════════

    useEffect(() => {
        if (!show || !payment) {
            return;
        }

        console.log('═══════════════════════════════════════════════');
        console.log('🟢 [PayAlicuotaPayPal] Modal abierto - Modo PayPal.me');
        console.log('═══════════════════════════════════════════════');

        setLoading(true);
        setError(null);
        setPaymentInitiated(false);

        const loadPayPalConfig = async () => {
            try {
                const token = localStorage.getItem('token');

                // Obtener el condominium_id del payment (no del usuario logueado)
                const condoId = payment.condominium_id;
                console.log('🔵 Buscando PayPal.me para condominium_id:', condoId);

                // Enviar condominium_id como query param
                const url = `${API_ENDPOINTS.condominiumPayPalClientId}?condominium_id=${condoId}`;
                const response = await fetch(url, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const data = await response.json();

                if (data.success && data.data && data.data.paypalMeUsername) {
                    console.log('✅ PayPal.me username cargado:', data.data.paypalMeUsername);
                    setPaypalMeUsername(data.data.paypalMeUsername);
                } else {
                    console.log('⚠️ Admin no ha configurado PayPal.me para condominio', condoId);
                    setError('El administrador no ha configurado PayPal para recibir pagos. Por favor, contacta al administrador.');
                }
            } catch (err) {
                console.error('❌ Error cargando config PayPal.me:', err);
                setError('Error al cargar configuración de pago. Intenta de nuevo.');
            } finally {
                setLoading(false);
            }
        };

        loadPayPalConfig();

    }, [show, payment]);

    // ═══════════════════════════════════════════════════════════════
    // ABRIR LINK DE PAYPAL.ME
    // ═══════════════════════════════════════════════════════════════

    const handlePayWithPayPal = () => {
        if (!paypalMeUsername || !payment) return;

        const amount = parseFloat(payment.amount).toFixed(2);
        const paypalMeUrl = `https://paypal.me/${paypalMeUsername}/${amount}USD`;

        console.log('🔗 Abriendo PayPal.me:', paypalMeUrl);

        // Abrir en nueva pestaña
        window.open(paypalMeUrl, '_blank', 'noopener,noreferrer');

        // Marcar que se inició el pago
        setPaymentInitiated(true);
    };

    // ═══════════════════════════════════════════════════════════════
    // HANDLERS
    // ═══════════════════════════════════════════════════════════════

    const handleClose = () => {
        setPaymentInitiated(false);
        onHide();
    };

    const handleUploadReceipt = () => {
        // Cerrar este modal y notificar al padre para mostrar modal de QR/comprobante
        if (onPaymentSuccess) {
            onPaymentSuccess(payment.id, 'upload_receipt');
        }
        handleClose();
    };

    // ═══════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════

    if (!payment) {
        return null;
    }

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={true}
            centered
            size="md"
        >
            {/* HEADER */}
            <Modal.Header
                closeButton
                style={{
                    background: 'linear-gradient(135deg, #003087 0%, #009cde 100%)',
                    color: 'white',
                    borderBottom: 'none'
                }}
            >
                <Modal.Title className="d-flex align-items-center">
                    <FaPaypal className="me-2" size={24} />
                    Pagar con PayPal.me
                </Modal.Title>
            </Modal.Header>

            {/* BODY */}
            <Modal.Body className="p-4">

                {/* Error */}
                {error && (
                    <Alert variant="danger" className="mb-3">
                        <FaTimesCircle className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* Loading */}
                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" className="mb-3" />
                        <p className="text-muted mb-0">Cargando configuración...</p>
                    </div>
                )}

                {/* Contenido principal */}
                {!loading && !error && (
                    <>
                        {/* Resumen del pago */}
                        <Card className="mb-4 border-0" style={{ backgroundColor: '#f8f9fa' }}>
                            <Card.Body className="p-3">
                                <h6 className="text-primary mb-3 fw-bold">
                                    <FaInfoCircle className="me-2" />
                                    Resumen del Pago
                                </h6>

                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">Concepto:</span>
                                    <strong>{payment.concept}</strong>
                                </div>

                                {payment.description && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">Descripción:</span>
                                        <span>{payment.description}</span>
                                    </div>
                                )}

                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">Fecha límite:</span>
                                    <span>{formatDate(payment.due_date)}</span>
                                </div>

                                {user && (
                                    <>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">
                                                <FaUser className="me-1" size={12} />
                                                Usuario:
                                            </span>
                                            <span>{user.name}</span>
                                        </div>

                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">
                                                <FaBuilding className="me-1" size={12} />
                                                Unidad:
                                            </span>
                                            <span>{user.apartment}</span>
                                        </div>
                                    </>
                                )}

                                <hr className="my-3" />

                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="h6 mb-0">Total a pagar:</span>
                                    <span className="h4 mb-0 fw-bold" style={{ color: '#003087' }}>
                                        {formatCurrency(payment.amount)} USD
                                    </span>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Estado: Antes de iniciar pago */}
                        {!paymentInitiated && (
                            <div className="text-center">
                                {/* Botón principal de PayPal */}
                                <Button
                                    onClick={handlePayWithPayPal}
                                    className="w-100 mb-3 py-3"
                                    style={{
                                        background: 'linear-gradient(135deg, #003087 0%, #009cde 100%)',
                                        border: 'none',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        borderRadius: '12px'
                                    }}
                                >
                                    <FaPaypal className="me-2" size={24} />
                                    Pagar con PayPal
                                    <FaExternalLinkAlt className="ms-2" size={14} />
                                </Button>

                                <div className="text-muted small">
                                    <FaInfoCircle className="me-1" />
                                    Se abrirá PayPal en una nueva pestaña
                                </div>
                            </div>
                        )}

                        {/* Estado: Después de iniciar pago */}
                        {paymentInitiated && (
                            <div className="text-center">
                                <Alert variant="success" className="mb-4">
                                    <FaCheckCircle className="me-2" size={20} />
                                    <strong>¡Ventana de PayPal abierta!</strong>
                                </Alert>

                                <Card className="border-0 mb-4" style={{ backgroundColor: '#fff3cd' }}>
                                    <Card.Body className="p-4">
                                        <h5 className="mb-3" style={{ color: '#856404' }}>
                                            <FaReceipt className="me-2" />
                                            ¿Ya completaste el pago?
                                        </h5>
                                        <p className="mb-3 text-muted">
                                            Después de pagar en PayPal, sube tu comprobante para que el administrador pueda validar tu pago.
                                        </p>
                                        <Button
                                            variant="warning"
                                            onClick={handleUploadReceipt}
                                            className="w-100 py-2"
                                            style={{ fontWeight: '600' }}
                                        >
                                            <FaUpload className="me-2" />
                                            Subir Comprobante de Pago
                                        </Button>
                                    </Card.Body>
                                </Card>

                                {/* Opción de volver a abrir PayPal */}
                                <Button
                                    variant="outline-primary"
                                    onClick={handlePayWithPayPal}
                                    className="w-100"
                                >
                                    <FaPaypal className="me-2" />
                                    Abrir PayPal nuevamente
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            {/* FOOTER */}
            <Modal.Footer className="border-0">
                <Button
                    variant="outline-secondary"
                    onClick={handleClose}
                >
                    {paymentInitiated ? 'Cerrar' : 'Cancelar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PayAlicuotaPayPal;
